import React, { useEffect, useState } from 'react';
import {
    DataTable,
    Table,
    TableHead,
    TableRow,
    TableHeader,
    TableBody,
    TableCell,
    TableExpandRow,
    TableExpandedRow,
    TableSelectAll,
    TableSelectRow,
    TableContainer,
    TableExpandHeader,
    Pagination,
} from 'carbon-components-react';
import {
    TextInput,
} from '@carbon/react';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { loadTasks, selectOneGUITasks, selectStatus, selectToken } from '../../../AppSlice';
import { JobStatuses } from './data.model';
import moment from 'moment';

const headers = [
    { key: 'title', header: 'Title' },
    { key: 'progressPct', header: 'Progress' },
    { key: 'status', header: 'Status' },
    { key: 'priority', header: 'Priority' },
    { key: 'createdAt', header: 'Created At' },
    { key: 'startedAt', header: 'Start Time' },
    { key: 'eta', header: 'ETA' },
    { key: 'vendor', header: 'System' },
];


const OneGUI: React.FC = () => {

    const dispatch = useAppDispatch()

    const [selectedStatus, setFilterStatus] = useState<typeof JobStatuses[number]>('All');
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const tasks = useAppSelector(selectOneGUITasks)
    const token = useAppSelector(selectToken)

    useEffect(() => {
        currentPage !== 1 && setCurrentPage(1)
    }, [selectedStatus])

    const loadJobs = (currentPage, rowsPerPage, selectedStatus) => {
        if (token) {
            dispatch(loadTasks({ token, page: currentPage - 1, size: rowsPerPage, status: selectedStatus }))
        }
    }

    useEffect(() => {
        loadJobs(currentPage, rowsPerPage, selectedStatus)

        const interval = setInterval(() => {
            loadJobs(currentPage, rowsPerPage, selectedStatus)
        }, 5000);

        return () => clearInterval(interval);

    }, [token, currentPage, rowsPerPage, selectedStatus])


    // const filteredTasks = filterStatus === 'All'
    //     ? tasks?.items
    //     : tasks?.items.filter((task) => task.status.toLowerCase() === filterStatus.toLowerCase());

    // const rows = filteredTasks.map((task) => ({ id: task.id, ...task }));


    // const totalItems = filteredTasks.length;
    // const startIndex = (currentPage - 1) * rowsPerPage;
    // const endIndex = startIndex + rowsPerPage;
    // const paginatedTasks = filteredTasks.slice(startIndex, endIndex);

    const getStatusClass = (status: typeof JobStatuses[number]): string => {
        switch (status) {
            case "COMPLETED":
                return 'progress-success';
            case "RUNNING":
                return 'progress-info';
            case "FAILED":
                return 'progress-danger';
            case "ERRORED":
                return 'progress-danger';
            case "QUEUED":
                return 'progress-warning';
            case "CANCELLED":
                return 'progress-cancelled';
            default:
                return 'progress-default';
        }
    };

    const Details = ({ taskId }: { taskId: string }) => {
        const row = tasks.items.find(item => item.id === taskId);
        if (!row) return null;
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <h4>Details for {row.title}</h4>
                <div><strong>ID:</strong> {row.id}</div>
                <div><strong>External ID:</strong> {row.externalJobId}</div>
                <div><strong>Priority:</strong> {row.priority}</div>
                <div><strong>Vendor:</strong> {row.vendor}</div>
                <div><strong>Input URI:</strong> {row.inputUri}%</div>
                <div><strong>Output URI:</strong> {row.outputUri}</div>
                <div><strong>Created At:</strong> {row.createdAt ? moment(row.createdAt).format("DD.MM.YYYY HH:mm:ss") : 'N/A'}</div>
                <div><strong>Submitted At:</strong> {row.submittedAt ? moment(row.submittedAt).format("DD.MM.YYYY HH:mm:ss") : 'N/A'}</div>
                <div><strong>Started At:</strong> {row.startedAt ? moment(row.startedAt).format("DD.MM.YYYY HH:mm:ss") : 'N/A'}</div>
                <div><strong>Finished At:</strong> {row.finishedAt ? moment(row.finishedAt).format("DD.MM.YYYY HH:mm:ss") : 'N/A'}</div>
                <div><strong>ETA:</strong> {row.eta ? moment(row.eta).format("DD.MM.YYYY HH:mm:ss") : 'N/A'}</div>
                <div><strong>Node:</strong> {row.meta.node}</div>
                <div><strong>Batch:</strong> {row.meta.batch}</div>
                <div><strong>Preset:</strong> {row.meta.preset}</div>
            </div>
        );
    }


    return (<>
        <div className="status-filters">
            {JobStatuses.map((status) => (
                <div
                    key={status}
                    className={`status-box ${getStatusClass(status)} ${status === selectedStatus ? 'active' : ''}`}
                    style={{ borderWidth: status === selectedStatus ? 3 : 1 }}
                    onClick={() => setFilterStatus(status)}
                >
                    {status}
                    <div style={{ marginTop: 4 }}>
                        {tasks?.totalsByStatus[status] || 0}
                    </div>
                </div>
            ))}
        </div>

        <div style={{ marginTop: 20, maxHeight: 'calc(100vh - 250px)', overflowY: 'auto' }}>
            <TableContainer>
                <DataTable
                    rows={tasks?.items || []}
                    headers={headers}
                    isSortable
                    render={({
                        rows,
                        headers,
                        getHeaderProps,
                        getRowProps,
                        getTableProps,
                        getSelectionProps,
                        getExpandHeaderProps,
                        getExpandRowProps,
                    }) => (

                        <Table {...getTableProps()} >
                            <TableHead>
                                <TableRow>
                                    <TableExpandHeader {...getExpandHeaderProps()} />
                                    {/* <TableSelectAll {...getSelectionProps()} /> */}
                                    {headers.map((header) => (
                                        <TableHeader key={header.key} {...getHeaderProps({ header })}>
                                            {header.header}
                                        </TableHeader>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody style={{ maxHeight: '500px !important', overflowY: 'auto' }}>
                                {rows.map((row) => (
                                    <React.Fragment key={row.id}>
                                        <TableExpandRow {...getRowProps({ row })}>
                                            {/* <TableSelectRow {...getSelectionProps({ row })} /> */}
                                            {/* {row.status} */}
                                            {row.cells.map((cell) => {
                                                if (["createdAt", "startedAt", "eta"].includes(cell.info.header)) {
                                                    return (
                                                        <TableCell key={cell.id}>
                                                            {cell.value ? moment(cell.value).format("DD.MM.YYYY HH:mm:ss") : "N/A"}
                                                        </TableCell>
                                                    );
                                                }

                                                else if (cell.info.header === 'progressPct') {
                                                    const statusClass = getStatusClass(row.cells.find(cell => cell.id === row.id + ":status").value);
                                                    const value = row.progress;

                                                    return (
                                                        <TableCell key={cell.id}>
                                                            <div className="progress-bar-container">
                                                                <div className={`progress-bar ${statusClass}`}>
                                                                    <div
                                                                        className="progress-bar-fill"
                                                                        style={{ width: `${cell.value}%` }}
                                                                    />
                                                                </div>
                                                                {/* <div className="progress-label">{cell.value}%</div> */}
                                                            </div>
                                                        </TableCell>
                                                    );
                                                }
                                                return <TableCell key={cell.id}>{cell.value}</TableCell>;
                                            })}


                                        </TableExpandRow>
                                        <TableExpandedRow colSpan={headers.length + 2}>
                                            <Details taskId={row.id} />
                                        </TableExpandedRow>
                                    </React.Fragment>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                />


            </TableContainer>
        </div>
        <Pagination
            totalItems={tasks.total || 0}
            pageSize={rowsPerPage}
            pageSizes={[5, 10, 20, 50]}
            page={currentPage}
            onChange={({ page, pageSize }) => {
                setCurrentPage(page);
                setRowsPerPage(pageSize);
            }}
        />
    </>
    );
};

export default OneGUI;
